import { createFileRoute, Link } from "@tanstack/react-router";
import { withMemberGate } from "@/lib/with-member-gate";
import { useInnerChild } from "@/components/inner-child/useInnerChild";
import { Loader2, Lock } from "lucide-react";

const TITLE = "My Saved Work — Recovery Trauma™";
const DESC = "Find your saved lists, reflections, journals and drawings in one private place.";

export const Route = createFileRoute("/my-saved-work")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex" },
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: withMemberGate(MySavedWork, "My Saved Work"),
});

const PLACES = [
  { to: "/journals", emoji: "📔", label: "My journals", hint: "All your journal entries", tint: "var(--qa-gold-tint, #fbefd2)" },
  { to: "/dreams", emoji: "🌙", label: "Dream Journal", hint: "Saved dreams and reflections", tint: "var(--qa-sky-tint, #e3f1fb)" },
  { to: "/art-board", emoji: "🎨", label: "My Art", hint: "Your saved drawings", tint: "var(--qa-rose-tint, #fde7ef)" },
  { to: "/creative-expression", emoji: "🌈", label: "Creative Expression", hint: "Saved exercises at the bottom", tint: "var(--qa-sage-tint, #e6f0e8)" },
  { to: "/inner-child", emoji: "🧸", label: "Inner Child Work", hint: "Lists, letters and reflections", tint: "var(--qa-lilac-tint, #ece6f6)" },
] as const;

const KIND_LABEL: Record<string, string> = {
  joy_list: "🌟 My Childhood Joy List",
  comfort_menu: "🫖 My Comfort Menu",
  letter: "💌 Letter to my inner child",
  reflection: "🪞 Reflection",
  exercise: "🧩 Exercise",
  check_in: "💗 Feelings check-in",
};

function MySavedWork() {
  const { entries, isLoading } = useInnerChild();
  return (
    <main className="mx-auto max-w-3xl px-4 pb-24 pt-6 sm:px-6">
      <h1 className="font-serif text-3xl text-foreground">My Saved Work</h1>
      <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
        <Lock className="h-4 w-4" aria-hidden /> Only you can see this. Nothing here is shared.
      </p>

      <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {PLACES.map((p) => (
          <Link
            key={p.to}
            to={p.to}
            className="flex min-h-[64px] items-center gap-3 rounded-2xl border border-gold/30 px-4 py-3 shadow-sm"
            style={{ background: p.tint }}
          >
            <span aria-hidden className="text-2xl">{p.emoji}</span>
            <span>
              <span className="block font-medium text-[oklch(0.25_0.02_60)]">{p.label}</span>
              <span className="block text-xs text-[oklch(0.35_0.02_60)]">{p.hint}</span>
            </span>
          </Link>
        ))}
      </div>

      <h2 className="mt-10 font-serif text-xl text-foreground">Inner Child Work</h2>
      {isLoading ? (
        <Loader2 className="mt-4 h-5 w-5 animate-spin text-muted-foreground" aria-label="Loading" />
      ) : entries.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">Nothing saved yet. Your lists and reflections will appear here once you save them.</p>
      ) : (
        <ul className="mt-4 space-y-3">
          {entries.map((e) => {
            const items = Array.isArray(e.data?.items) ? (e.data.items as string[]) : [];
            return (
              <li key={e.id} className="rounded-2xl border border-border/60 bg-card p-4">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-medium text-foreground">{(e.kind === "joy_list" || e.kind === "comfort_menu" ? KIND_LABEL[e.kind] : e.title) || KIND_LABEL[e.kind] || "Saved entry"}</span>
                  <span className="text-xs text-muted-foreground">
                    {new Date(e.updated_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                  </span>
                </div>
                {items.length > 0 && (
                  <ul className="mt-2 flex flex-wrap gap-1.5">
                    {items.map((i) => (
                      <li key={i} data-saved="true" className="rt-choice rounded-full px-2.5 py-1 text-xs font-medium">{i}</li>
                    ))}
                  </ul>
                )}
                {e.content && <p className="mt-2 line-clamp-3 whitespace-pre-wrap text-sm text-foreground/80">{e.content}</p>}
              </li>
            );
          })}
        </ul>
      )}
      <Link to="/inner-child" className="mt-4 inline-block text-sm font-medium text-foreground underline underline-offset-4">Open Inner Child Work to edit →</Link>
    </main>
  );
}
